import type { ReactNode } from "react";
import type { AppViewStatus } from "../../shared/types.ts";
import { cn } from "./cn";

export type Tone = "brand" | "mint" | "accent" | "cream" | "amber" | "gray" | "red";

const TONE_CLASS: Record<Tone, string> = {
  brand: "bg-tone-brand text-tone-brand-ink",
  mint: "bg-tone-mint text-tone-mint-ink",
  accent: "bg-tone-accent text-tone-accent-ink",
  cream: "bg-tone-cream text-tone-cream-ink",
  amber: "bg-tone-amber text-tone-amber-ink",
  gray: "bg-tone-gray text-tone-gray-ink",
  red: "bg-tone-red text-tone-red-ink",
};

export const APP_STATUS: Record<AppViewStatus, { label: string; tone: Tone; pulse?: boolean }> = {
  pending: { label: "Queued", tone: "amber" },
  queued: { label: "Queued", tone: "amber" },
  applying: { label: "Sending", tone: "amber", pulse: true },
  submitted: { label: "Applied", tone: "brand" },
  confirmed: { label: "Confirmed", tone: "brand" },
  failed: { label: "Didn't go through", tone: "red" },
  skipped: { label: "Skipped", tone: "gray" },
  on_site: { label: "Applied on site", tone: "brand" },
};

export function ToneDot({ pulse, className }: { pulse?: boolean; className?: string }) {
  return <span aria-hidden className={cn("relative inline-flex size-1.5 shrink-0 rounded-full bg-current", pulse && "pulse-dot", className)} />;
}

export type StatusBadgeProps = {
  tone?: Tone;
  dot?: boolean;
  pulse?: boolean;
  className?: string;
  children: ReactNode;
};

/** A near-square tinted label for a state: Applied, Interview, Offer. */
export function StatusBadge({ tone = "gray", dot = false, pulse, className, children }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex h-6 w-fit shrink-0 items-center gap-1.5 rounded-chip px-2 text-xs font-semibold whitespace-nowrap",
        TONE_CLASS[tone],
        className,
      )}
    >
      {dot || pulse ? <ToneDot pulse={pulse} /> : null}
      {children}
    </span>
  );
}

/** `StatusBadge` for an application's status, with the house label and tone. */
export function AppStatusBadge({ status, className }: { status: AppViewStatus; className?: string }) {
  const meta = APP_STATUS[status];
  return (
    <StatusBadge tone={meta.tone} pulse={meta.pulse} className={className}>
      {meta.label}
    </StatusBadge>
  );
}
